import { Box, Stack, Typography } from '@mui/material';
import { formatIbanDisplay } from '../../../utils/iban';
import type { BookingLine, BookingMoneyTotals } from '../pricing/pricingEngine';

type BookingSummaryProps = {
  participantsCount?: number | null;
  bookingLines?: BookingLine[] | null;
  moneyTotals?: Partial<BookingMoneyTotals> | null;
  amount: number;
};

type Props = {
  iban: string | null | undefined;
  amount: number;
  reference: string;
  participantsCount?: number | null;
  bookingLines?: BookingLine[] | null;
  moneyTotals?: Partial<BookingMoneyTotals> | null;
};

function formatEuro(amount: number) {
  return `${amount} EUR`;
}

export function BookingPricingSummary({ participantsCount, bookingLines, moneyTotals, amount }: BookingSummaryProps) {
  const normalizedLines = Array.isArray(bookingLines) ? bookingLines.filter((line) => line.quantityInitial > 0) : [];
  if (normalizedLines.length === 0) {
    return null;
  }

  const totalParticipants = typeof participantsCount === 'number'
    ? participantsCount
    : typeof moneyTotals?.initialTotal === 'number'
      ? normalizedLines.reduce((sum, line) => sum + line.quantityInitial, 0)
      : normalizedLines.reduce((sum, line) => sum + line.quantityInitial, 0);
  const totalAmount = typeof moneyTotals?.initialTotal === 'number' ? moneyTotals.initialTotal : amount;

  return (
    <Box sx={{ mt: 2.5, p: 2.5, borderRadius: 3, bgcolor: 'rgba(28,46,36,0.03)', border: '1px solid rgba(28,46,36,0.08)' }}>
      <Stack spacing={1.25}>
        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.12em' }}>
          Recapitulatif
        </Typography>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography>Participants : <b>{totalParticipants}</b></Typography>
          <Typography>Total : <b>{formatEuro(totalAmount)}</b></Typography>
        </Stack>
        {normalizedLines.map((line) => (
          <Stack key={line.rateId} direction="row" justifyContent="space-between" spacing={2} sx={{ fontSize: 14 }}>
            <Typography sx={{ flex: 1 }}>{line.labelSnapshot}</Typography>
            <Typography sx={{ whiteSpace: 'nowrap' }}>{line.quantityInitial} x {line.unitPriceSnapshot > 0 ? formatEuro(line.unitPriceSnapshot) : 'Gratuit'}</Typography>
            <Typography sx={{ whiteSpace: 'nowrap' }}><b>{line.lineAmountInitial > 0 ? formatEuro(line.lineAmountInitial) : 'Gratuit'}</b></Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

export function BankTransferInstructions({ iban, amount, reference, participantsCount, bookingLines, moneyTotals }: Props) {
  if (!iban) return null;

  const formattedIban = formatIbanDisplay(iban);

  return (
    <Box sx={{ p: 3, borderRadius: 3, bgcolor: 'rgba(192,110,82,0.06)', border: '1px solid rgba(192,110,82,0.16)' }}>
      <Typography variant="h6" sx={{ mb: 1.5, color: 'text.primary' }}>
        Paiement par virement
      </Typography>
      <Typography sx={{ mb: 1.5 }}>
        Merci d'effectuer un virement de <b>{amount} EUR</b>
      </Typography>

      <BookingPricingSummary
        participantsCount={participantsCount}
        bookingLines={bookingLines}
        moneyTotals={moneyTotals}
        amount={amount}
      />

      <Stack spacing={1.5}>
        <Typography>
          Compte : <b>{formattedIban}</b>
        </Typography>

        <Typography>
          Communication : <b>{reference}</b>
        </Typography>
      </Stack>
    </Box>
  );
}

export default BankTransferInstructions;